import {
  Guest,
  SelectionGroupType,
  SelectionOrExtraType,
  SelectionType,
  SpaPackageBasketItem,
} from 'shared-types/Basket';
import { SpaPackageType } from 'shared-types/SpaPackage';
import {
  ExtraFormType,
  ExtrasFormDataType,
  GuestFormData,
  SelectionDataType,
  SpaPackageFormDataType,
} from '../SpaPackageModal.types';

type SelectionTypeWithConfGroupId = SelectionType & { id: number };

const hasOptionsPerGuest = (entity: SpaPackageType, id: number) =>
  !!entity.configurationGroups.find((cg) => cg.id === id)?.optionsPerGuest;

const isSelected = (entity: SpaPackageType, selectItem: SelectionDataType): boolean => {
  const { configurationGroupId, isSelected, timeslot } = selectItem;
  if (entity.configurationGroupsRequireTimeslots) {
    const configurationGroup = entity.configurationGroups?.find(
      (cg) => cg.id === configurationGroupId
    );
    return configurationGroup?.supportsSelections ? isSelected : !!timeslot;
  }
  return isSelected;
};

export const parseGuestDetails = (
  guest: GuestFormData
): Pick<Guest, 'id' | 'title' | 'firstName' | 'lastName' | 'gender' | 'loggedInUser'> => ({
  ...(guest.id !== undefined && { id: guest.id }),
  ...(guest.loggedInUser && { loggedInUser: guest.loggedInUser }),
  title: guest.title,
  firstName: guest.firstName,
  lastName: guest.lastName,
  gender: guest.gender,
});

const parseSelectionGroups = (
  guests: GuestFormData[],
  entity: SpaPackageType,
  date: string
): SelectionGroupType[] => {
  const guestSelections: SelectionTypeWithConfGroupId[] = guests.flatMap((guest) => {
    const guestDetailsData = parseGuestDetails(guest);
    const selected = Object.values(guest.configurationGroups || {}).flatMap((cg) =>
      cg.filter(
        (selectItem): selectItem is SelectionDataType =>
          selectItem != null && isSelected(entity, selectItem)
      )
    );
    return selected.map((sel) => ({
      id: sel.configurationGroupId,
      option: { id: sel.optionId },
      ...(sel.timeslot && { startTime: sel.timeslot.startTime }),
      ...(sel.timeslot && { endTime: sel.timeslot.endTime }),
      ...(sel.timeslot && { startDate: date }),
      ...(hasOptionsPerGuest(entity, sel.configurationGroupId) && {
        guestDetails: guestDetailsData,
      }),
    }));
  });
  return guestSelections.reduce((acc: SelectionGroupType[], cur: SelectionTypeWithConfGroupId) => {
    let selectionGroup: SelectionGroupType | undefined = acc.find(
      (sg) => sg.configurationGroup.id === cur.id
    );
    if (!selectionGroup) {
      selectionGroup = {
        configurationGroup: {
          id: cur.id,
        },
        selections: [],
      };
    }
    const { id, ...selection } = cur;
    selectionGroup.selections.push(selection as SelectionType);
    return [...acc.filter((sg) => sg.configurationGroup.id !== id), selectionGroup];
  }, []);
};

export const addExtras = (
  extras: ExtraFormType[],
  selectionGroups: SelectionGroupType[]
): SelectionOrExtraType[] => [
  ...selectionGroups,
  {
    isExtras: true,
    selections: extras.map((extra) => ({
      extra: { id: extra.id },
      guestDetails: parseGuestDetails(extra.value),
    })),
  },
];

const parseSelectionsAndExtras = (
  itemGuests: GuestFormData[],
  entity: SpaPackageType,
  itemExtras: ExtraFormType[],
  date: string
): SelectionOrExtraType[] => {
  const selectionGroups = parseSelectionGroups(itemGuests, entity, date);
  const selectionGroupsWithExtras = addExtras(itemExtras, selectionGroups);
  return selectionGroupsWithExtras;
};

export const filterExtrasForItem = (
  itemGuests: GuestFormData[],
  allFormExtras?: ExtrasFormDataType
): ExtraFormType[] => {
  const stringify = (
    guestData: Pick<Guest, 'id' | 'title' | 'firstName' | 'lastName' | 'gender'>
  ) => {
    const { id, title, firstName, lastName, gender } = guestData;
    const idd = id || '';
    return idd.toString().concat(title || '', firstName, lastName, gender);
  };

  const guestStrings = itemGuests.map((guest) => {
    const { configurationGroups: _, externalSpaId: __, ...guestData } = guest;
    return stringify(guestData);
  });

  return Object.values(allFormExtras || {}).flatMap((extraSelections) =>
    extraSelections.filter((extra) => {
      const guestString = stringify(extra.value);
      return guestStrings.includes(guestString);
    })
  );
};

export const parseSpaPackageData = (
  data: SpaPackageFormDataType,
  entity: SpaPackageType,
  groupGuestsInSingleItem = false
): SpaPackageBasketItem[] => {
  const startTime = data.arrivalTime ? data.arrivalTime.startTime.trim() : '';
  const endTime = data.arrivalTime ? data.arrivalTime.endTime.trim() : '';
  const checkIn = `${data.date} ${startTime}`.trimEnd();
  const checkOut = `${data.date} ${endTime}`.trimEnd();

  if (groupGuestsInSingleItem) {
    const allGuests = data.guests;
    const allExtras = filterExtrasForItem(allGuests, data.extras);

    return [
      {
        type: 'spa-packages',
        spaPackage: {
          id: entity.id,
        },
        checkIn,
        checkOut,
        guestDetails: allGuests.map(parseGuestDetails),
        ...(data.note !== undefined && { note: data.note }),
        selectionGroups: parseSelectionsAndExtras(allGuests, entity, allExtras, data.date),
      },
    ];
  }

  // Legacy behavior: split guests into separate items
  return new Array(data.guests.length / entity.numberOfGuests).fill('').map((_, index) => {
    const start = index * entity.numberOfGuests;
    const itemGuests = data.guests.slice(start, start + entity.numberOfGuests);
    const itemExtras = filterExtrasForItem(itemGuests, data.extras);
    return {
      type: 'spa-packages',
      spaPackage: {
        id: entity.id,
      },
      checkIn,
      checkOut,
      guestDetails: itemGuests.map(parseGuestDetails),
      ...(data.note !== undefined && { note: data.note }),
      selectionGroups: parseSelectionsAndExtras(itemGuests, entity, itemExtras, data.date),
    };
  });
};
